import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Plus, Save } from 'lucide-react';
import { observer } from 'mobx-react';
import { injectTSDI } from '@core/tsdi';
import { Api } from '@core/api';
import {
    CommunityWhitelabelDashboardWidgetDto,
    CommunityWhitelabelDashboardWidgetLayout,
    CommunityDetailsSupportDto,
    I18NText
} from '@api/ChallengeClient';
import { FormSection } from '@components/form-section';
import { Spinner } from '@components/spinner';
import { StickyBar } from '@components/sticky-bar';
import { TextField } from '@components/textfield';
import { Alert, AlertDescription } from '@components/ui/alert';
import { Button } from '@components/ui/button';
import { Label } from '@components/ui/label';
import { Switch } from '@components/ui/switch';
import { DashboardWidgetEditor } from './dashboard-widget-editor';
import { DashboardWidgetPreview } from './dashboard-widget-preview';
import { imageUrl } from './widget-content';

type EditedWidget = CommunityWhitelabelDashboardWidgetDto & { key: string };

let nextWidgetKey = 0;

const Hint = ({ children }: { children: React.ReactNode }) => (
    <p className="text-xs text-muted-foreground">{children}</p>
);

function editedWidget(
    widget: CommunityWhitelabelDashboardWidgetDto
): EditedWidget {
    nextWidgetKey += 1;
    return { ...widget, key: `widget-${nextWidgetKey}` };
}

function trimmedText(text?: I18NText): I18NText | undefined {
    const entries = Object.entries(text ?? {})
        .map(([language, value]) => [language, (value ?? '').trim()])
        .filter(([, value]) => value);
    return entries.length > 0 ? Object.fromEntries(entries) : undefined;
}

function widgetToSave({
    enabled,
    layout,
    title,
    text,
    image,
    link,
    buttonLabel,
    visibleFrom,
    visibleTo
}: EditedWidget): CommunityWhitelabelDashboardWidgetDto {
    return {
        enabled,
        layout,
        title: trimmedText(title),
        text: trimmedText(text),
        image,
        link: link?.trim() || undefined,
        buttonLabel: trimmedText(buttonLabel),
        visibleFrom,
        visibleTo
    };
}

const CommunityWhitelabel = observer(() => {
    const { communityId } = useParams();
    const { challengeClient } = injectTSDI(Api);
    const [community, setCommunity] =
        useState<CommunityDetailsSupportDto | null>(null);
    const [whitelabelId, setWhitelabelId] = useState('');
    const [whitelabelEnabled, setWhitelabelEnabled] = useState(true);
    const [whitelabelShortName, setWhitelabelShortName] = useState('');
    const [widgets, setWidgets] = useState<EditedWidget[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    const applyCommunity = (data: CommunityDetailsSupportDto) => {
        setCommunity(data);
        setWhitelabelId(data.whitelabelId ?? '');
        setWhitelabelEnabled(data.whitelabelEnabled ?? true);
        setWhitelabelShortName(data.whitelabelShortName ?? '');
        setWidgets((data.dashboardWidgets ?? []).map(editedWidget));
    };

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        challengeClient.support
            .communitySupportControllerV2Get(parseInt(communityId!, 10))
            .then((response) => {
                if (!cancelled) {
                    applyCommunity(response.data);
                }
            })
            .catch((err) => {
                if (!cancelled) {
                    setError(err.message || 'Failed to load the community');
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [challengeClient, communityId]);

    const changeWidgets = (
        change: (current: EditedWidget[]) => EditedWidget[]
    ) => {
        setSaved(false);
        setWidgets(change);
    };

    const updateWidget = (
        key: string,
        patch: Partial<CommunityWhitelabelDashboardWidgetDto>
    ) =>
        changeWidgets((current) =>
            current.map((widget) =>
                widget.key === key ? { ...widget, ...patch } : widget
            )
        );

    const moveWidget = (position: number, offset: number) =>
        changeWidgets((current) => {
            const moved = [...current];
            const [widget] = moved.splice(position, 1);
            moved.splice(position + offset, 0, widget);
            return moved;
        });

    const removeWidget = (key: string) =>
        changeWidgets((current) =>
            current.filter((widget) => widget.key !== key)
        );

    const addWidget = () =>
        changeWidgets((current) => [
            ...current,
            editedWidget({
                enabled: true,
                layout: CommunityWhitelabelDashboardWidgetLayout.HERO
            })
        ]);

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            const response =
                await challengeClient.support.communitySupportControllerUpdateWhitelabel(
                    community!.id,
                    {
                        whitelabelId: whitelabelId.trim() || undefined,
                        whitelabelEnabled,
                        whitelabelShortName:
                            whitelabelShortName.trim() || undefined,
                        dashboardWidgets: widgets.map(widgetToSave)
                    }
                );
            applyCommunity(response.data);
            setSaved(true);
        } catch (err: any) {
            setError(
                err.response?.data?.message ||
                    err.message ||
                    'Failed to save the community whitelabel'
            );
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center p-12">
                <Spinner />
            </div>
        );
    }

    if (!community) {
        return (
            <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
            </Alert>
        );
    }

    return (
        <div className="flex flex-col gap-6 pb-24">
            {error && (
                <Alert variant="destructive">
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            )}
            {saved && (
                <Alert>
                    <AlertDescription>Saved.</AlertDescription>
                </Alert>
            )}

            <Hint>
                Name, logo, cover image and colour come from the Details tab.
            </Hint>

            <FormSection title="Whitelabel">
                <div className="flex flex-col gap-1.5 md:max-w-md">
                    <div className="flex items-center gap-2">
                        <Switch
                            id="whitelabel-enabled"
                            checked={whitelabelEnabled}
                            onCheckedChange={(enabled) => {
                                setSaved(false);
                                setWhitelabelEnabled(enabled);
                            }}
                        />
                        <Label htmlFor="whitelabel-enabled">
                            Whitelabel enabled
                        </Label>
                    </div>
                    <Hint>
                        Switched off, the whitelabel ID and widgets are kept,
                        but members get no whitelabel tab, widgets or branding,
                        and Smart Connect cannot switch it on.
                    </Hint>
                    <TextField
                        label="Whitelabel ID"
                        type="text"
                        value={whitelabelId}
                        onChange={(value) => {
                            setSaved(false);
                            setWhitelabelId(String(value));
                        }}
                    />
                    <Hint>
                        Smart Connect links with this whitelabel (wl) switch on
                        this community whitelabel for the members who join.
                    </Hint>
                    <TextField
                        label="Short name"
                        type="text"
                        placeholder={community.name}
                        value={whitelabelShortName}
                        onChange={(value) => {
                            setSaved(false);
                            setWhitelabelShortName(String(value));
                        }}
                    />
                    <Hint>
                        Optional. The label of the community tab in the
                        app&apos;s bottom bar, where a long community name gets
                        cut off. Keep it to about 12 characters.
                    </Hint>
                </div>
            </FormSection>

            <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,1fr)_auto]">
                <FormSection title="Dashboard widgets">
                    <div className="flex flex-col gap-3">
                        <Hint>
                            Each enabled widget is a card on members&apos; Home,
                            in this order, while its visibility window is open.
                        </Hint>
                        {widgets.map((widget, position) => (
                            <DashboardWidgetEditor
                                key={widget.key}
                                id={widget.key}
                                communityId={community.id}
                                position={position}
                                count={widgets.length}
                                widget={widget}
                                onChange={(patch) =>
                                    updateWidget(widget.key, patch)
                                }
                                onMove={(offset) =>
                                    moveWidget(position, offset)
                                }
                                onRemove={() => removeWidget(widget.key)}
                            />
                        ))}
                        <Button
                            variant="outline"
                            className="self-start"
                            onClick={addWidget}
                        >
                            <Plus />
                            Add widget
                        </Button>
                    </div>
                </FormSection>
                <div className="xl:sticky xl:top-4">
                    <DashboardWidgetPreview
                        branding={{
                            name: community.name,
                            logoUrl: imageUrl(community.photo),
                            accentColor: community.color ?? '#c8ff00'
                        }}
                        widgets={widgets}
                    />
                </div>
            </div>

            <StickyBar>
                <Button onClick={handleSave} loading={saving}>
                    <Save />
                    Save
                </Button>
            </StickyBar>
        </div>
    );
});

export default CommunityWhitelabel;
